import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { RequireAdmin, RequireSeller, RequireUser } from '@/auth/guards';
import { PageBoundary } from '@/components/shell/PageBoundary';
import { ShellLayout } from '@/components/shell/ShellLayout';
import { PageSpinner } from '@/components/ui/PageSpinner';

const LandingPage = lazy(() => import('@/pages/landing/LandingPage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const ChangePasswordPage = lazy(() => import('@/pages/auth/ChangePasswordPage'));
const StaffPage = lazy(() => import('@/pages/staff/StaffPage'));
const SellerSupportPage = lazy(() => import('@/pages/admin/SellerSupportPage'));
const OnboardSellerPage = lazy(() => import('@/pages/admin/OnboardSellerPage'));
const PriceRunsPage = lazy(() => import('@/pages/admin/PriceRunsPage'));
const SellerHomePage = lazy(() => import('@/pages/seller/SellerHomePage'));
const ProductsPage = lazy(() => import('@/pages/seller/ProductsPage'));
const ProductPage = lazy(() => import('@/pages/seller/ProductPage'));
const SetsPage = lazy(() => import('@/pages/seller/SetsPage'));
const IssuesPage = lazy(() => import('@/pages/seller/IssuesPage'));
const IssuePage = lazy(() => import('@/pages/seller/IssuePage'));
const ReviewsPage = lazy(() => import('@/pages/seller/ReviewsPage'));
const SetPage = lazy(() => import('@/pages/seller/SetPage'));
const SetBuilderPage = lazy(() => import('@/pages/seller/SetBuilderPage'));
const ListSetPage = lazy(() => import('@/pages/seller/ListSetPage'));
const DataPage = lazy(() => import('@/pages/seller/DataPage'));
const WalletPage = lazy(() => import('@/pages/seller/WalletPage'));
const NameYourPricePage = lazy(() => import('@/pages/seller/NameYourPricePage'));
const GroupBuysPage = lazy(() => import('@/pages/seller/GroupBuysPage'));
const SettingsPage = lazy(() => import('@/pages/seller/SettingsPage'));
const CampaignsPage = lazy(() => import('@/pages/seller/CampaignsPage'));
const CampaignPage = lazy(() => import('@/pages/seller/CampaignPage'));
const CampaignBuilderPage = lazy(() => import('@/pages/seller/CampaignBuilderPage'));
const IntentsPage = lazy(() => import('@/pages/seller/IntentsPage'));
const PricesPage = lazy(() => import('@/pages/seller/PricesPage'));
const FloorsPage = lazy(() => import('@/pages/seller/FloorsPage'));
const QualityPage = lazy(() => import('@/pages/seller/QualityPage'));
const ActivityPage = lazy(() => import('@/pages/seller/ActivityPage'));
const SelectSellerPage = lazy(() => import('@/pages/seller/SelectSellerPage'));
const NoSellerPage = lazy(() => import('@/pages/seller/NoSellerPage'));

/** An unknown path under a seller goes to that seller's Home. */
function BackToSellerHome() {
  const { sellerId } = useParams<{ sellerId: string }>();
  return <Navigate to={`/${sellerId}`} replace />;
}

// Public: the landing page and sign-in. Everything else needs a session; seller screens
// live under /:sellerId and the staff page under /staff. ShellLayout (sidebar and toolbar) is
// rendered once per section, so moving between pages swaps only the page area; PageBoundary
// sits above every signed-in page (spec S00: an error boundary per page).
export default function App() {
  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route element={<RequireUser />}>
          <Route element={<PageBoundary />}>
            <Route path="/account/password" element={<ChangePasswordPage />} />
          </Route>
          <Route element={<ShellLayout />}>
            <Route element={<PageBoundary />}>
              <Route path="/select-seller" element={<SelectSellerPage />} />
              <Route path="/no-seller" element={<NoSellerPage />} />
              <Route element={<RequireAdmin />}>
                <Route path="/staff" element={<StaffPage />} />
                <Route path="/admin/sellers" element={<SellerSupportPage />} />
                <Route path="/admin/sellers/new" element={<OnboardSellerPage />} />
                <Route path="/admin/prices" element={<PriceRunsPage />} />
              </Route>
            </Route>
          </Route>
          <Route path="/:sellerId" element={<RequireSeller />}>
            <Route element={<ShellLayout />}>
              <Route element={<PageBoundary />}>
                <Route index element={<SellerHomePage />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="products/:productId" element={<ProductPage />} />
                <Route path="sets" element={<SetsPage />} />
                <Route path="issues" element={<IssuesPage />} />
                <Route path="issues/:issueType" element={<IssuePage />} />
                <Route path="reviews" element={<ReviewsPage />} />
                <Route path="sets/new" element={<SetBuilderPage />} />
                <Route path="sets/list" element={<ListSetPage />} />
                <Route path="sets/:setId" element={<SetPage />} />
                <Route path="sets/:setId/edit" element={<SetBuilderPage />} />
                <Route path="sets/:setId/edit-list" element={<ListSetPage />} />
                <Route path="data" element={<DataPage />} />
                <Route path="wallet" element={<WalletPage />} />
                <Route path="deals/name-your-price" element={<NameYourPricePage />} />
                <Route path="deals/group-buys" element={<GroupBuysPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="campaigns" element={<CampaignsPage />} />
                <Route path="campaigns/:campaignId" element={<CampaignPage />} />
                <Route path="campaigns/:campaignId/build" element={<CampaignBuilderPage />} />
                <Route path="intents" element={<IntentsPage />} />
                <Route path="prices" element={<PricesPage />} />
                <Route path="floors" element={<FloorsPage />} />
                <Route path="quality" element={<QualityPage />} />
                <Route path="activity" element={<ActivityPage />} />
                <Route path="*" element={<BackToSellerHome />} />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
